import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { fetchPushSubscriptions } from "../../api/client";
import {
  formatDeviceLastSeen,
  readStoredPushEndpoint,
  registerWebPushOnDevice,
  removeRemoteWebPushDevice,
  resolveDeviceDisplayLabel,
  isWebPushSupported,
} from "../../notifications/webPushClient";

export default function WebPushDeviceList({ onRegisterSuccess }) {
  const { t } = useTranslation("notifications");
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [error, setError] = useState(false);
  const localEndpoint = readStoredPushEndpoint();

  const loadDevices = useCallback(async () => {
    if (!isWebPushSupported()) return;
    setLoading(true);
    setError(false);
    try {
      const rows = await fetchPushSubscriptions();
      setDevices(Array.isArray(rows) ? rows : []);
    } catch {
      setError(true);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDevices();
  }, [loadDevices]);

  const handleRemove = async (device) => {
    if (!device?.id) return;
    setRemovingId(device.id);
    try {
      await removeRemoteWebPushDevice({ id: device.id, endpoint: device.endpoint });
      await loadDevices();
    } catch {
      /* ignore */
    } finally {
      setRemovingId(null);
    }
  };

  const handleRefreshThisDevice = async () => {
    try {
      await registerWebPushOnDevice({ requestPermission: false });
      await loadDevices();
      onRegisterSuccess?.();
    } catch {
      /* ignore */
    }
  };

  if (!isWebPushSupported()) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {t("settings.devicesTitle")}
        </Typography>
        <IconButton size="small" aria-label={t("settings.devicesRefresh")} onClick={loadDevices}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t("settings.devicesHelp")}
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : error ? (
        <Typography variant="body2" color="error">
          {t("settings.devicesLoadErr")}
        </Typography>
      ) : devices.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("settings.devicesEmpty")}
        </Typography>
      ) : (
        <List dense disablePadding sx={{ bgcolor: "action.hover", borderRadius: 2, px: 1 }}>
          {devices.map((device) => {
            const isCurrent =
              Boolean(localEndpoint && device.endpoint && localEndpoint === device.endpoint);
            const label = resolveDeviceDisplayLabel(device);
            const seen = formatDeviceLastSeen(device.updated_at || device.created_at);
            return (
              <ListItem
                key={device.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label={t("settings.devicesRemoveAria", { device: label })}
                    disabled={removingId === device.id}
                    onClick={() => handleRemove(device)}
                  >
                    {removingId === device.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DeleteOutlineIcon fontSize="small" />
                    )}
                  </IconButton>
                }
                sx={{ py: 1 }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" fontWeight={700}>
                        {label}
                      </Typography>
                      {isCurrent ? (
                        <Chip
                          size="small"
                          label={t("settings.devicesThisDevice")}
                          color="primary"
                          variant="outlined"
                        />
                      ) : null}
                    </Stack>
                  }
                  secondary={
                    seen ? t("settings.devicesLastSeen", { date: seen }) : undefined
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}

      <Button
        size="small"
        variant="text"
        onClick={handleRefreshThisDevice}
        sx={{ mt: 1, textTransform: "none", fontWeight: 600 }}
      >
        {t("settings.devicesSyncThis")}
      </Button>
    </Box>
  );
}
